"use client";

import { useRef } from "react";
import Image from "next/image";

import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { CTAButton } from "@/shadcn/ui/button";
import { HLSVideoPlayer } from "@/components/ui/SanityVideoPlayer";
import { PortableText } from "@portabletext/react";
import { customRichTextComponent } from "@/sanity/sanityPortableText";

import { PageHomeSchema } from "@/sanity/pages/PageHomeTemplate";

import logoFullImg from "@/public/lp2/global/logo-full.svg";
import gridGBimg from "@/public/lp2/green-banner/grid-bg.svg";
import blurBGimg from "@/public/lp2/green-banner/blur-bg.svg";
import bitunixLogoImg from "@/public/lp2/global/logo-bitunix.svg";
import phemexLogoImg from "@/public/lp2/global/logo-phemex.svg";

if (typeof window !== "undefined") {
  gsap.registerPlugin(ScrollTrigger, useGSAP);
}

export default function GreenBannerSection({
  data,
  bitunix,
  phemex,
}: {
  data: PageHomeSchema;
  bitunix?: boolean;
  phemex?: boolean;
}) {
  const containerRef = useRef<HTMLDivElement | any>(undefined);
  const contentRef = useRef<HTMLDivElement | any>(undefined);

  useGSAP(
    () => {
      const greenbannerTL = gsap.timeline({
        defaults: { opacity: 0, ease: "power2.inOut", duration: 0.25 },
      });

      greenbannerTL
        .from(containerRef.current, {
          y: "2rem",
          delay: 0.5,
          scrollTrigger: {
            trigger: containerRef.current,
            end: "center center",
            scrub: 1,
          },
        })
        .from(contentRef.current.children, {
          y: "2rem",
          delay: 0.5,
          stagger: 0.1,
          scrollTrigger: {
            trigger: containerRef.current,
            end: "center center",
            scrub: 1,
          },
        });
    },
    { scope: containerRef }
  );

  return (
    <section ref={containerRef} className="w-full">
      <div className="container-lp mx-auto lg:!p-[20rem] pt-[15rem]">
        <div className="green-card-section !h-full rounded-[3rem] flex flex-col items-center justify-center w-full py-[10rem] lg:py-[15rem] px-[2rem] lg:px-[13rem] relative">
          {/* Backgrounds */}
          <Image
            className="absolute z-[-1] inset-0 w-full h-full pointer-events-none object-cover rounded-[3rem]"
            alt="Grid background"
            src={gridGBimg}
          />
          <Image
            className="green-card-section-blur-img absolute z-[-2] inset-0 w-full h-full pointer-events-none"
            alt="Blur background"
            src={blurBGimg}
          />
          <div className="green-card-section-blur absolute z-[-2] mx-auto w-[80%] h-[65%] pointer-events-none rounded-[23.6875rem]"></div>
          <div className="absolute inset-0 z-[-2] pointer-events-none rounded-[4.4rem] overflow-hidden">
            <HLSVideoPlayer
              controls={false}
              playsInline={true}
              autoPlay={true}
              muted={true}
              loop={true}
              className="!w-full !h-full object-cover pointer-events-none"
              source={{
                embed: false,
                muxVideo: {
                  assetId: "",
                  filename: "video-bg",
                  status: "ready",
                  playbackId: "T02VT02sMc9eTvJcCyP00FizzEcb2eSRE1kTJwO1r9y502g",
                },
                embedUrl: "",
                thumbnail: {
                  src: "",
                  width: 0,
                  height: 0,
                  lqip: "",
                  name: "",
                },
              }}
            />
          </div>
          {/* End backgrounds */}

          <div
            ref={contentRef}
            className="w-full flex flex-col items-center justify-center"
          >
            {bitunix ? (
              <Image
                className="w-[30rem] lg:w-[50rem] mb-[5rem]"
                alt="Logo"
                src={bitunixLogoImg}
              />
            ) : phemex ? (
              <Image
                className="w-[30rem] lg:w-[50rem] mb-[5rem]"
                alt="Logo"
                src={phemexLogoImg}
              />
            ) : (
              <Image
                className="h-[4.125rem] lg:h-[5.625rem] mb-[5rem]"
                alt="Logo"
                src={logoFullImg}
              />
            )}

            {data?.greenTexts && (
              <PortableText
                value={data?.greenTexts ?? []}
                components={customRichTextComponent({
                  block: {
                    h2: ({ children }) => (
                      <h2 className="gradient-title text-[4rem] lg:text-[7rem] font-[600] tracking-[-0.16rem] lg:tracking-[-0.28rem] leading-[120%] text-center mb-[2rem] lg:w-[95%]">
                        {children}
                      </h2>
                    ),
                    normal: ({ children }) => (
                      <p className="text-white text-center text-[2.25rem] font-[400] leading-[3.375rem] opacity-70 mb-[5rem] lg:w-[55%]">
                        {children}
                      </p>
                    ),
                  },
                })}
              />
            )}

            {data?.greenCTA && <CTAButton data={data?.greenCTA} />}
          </div>
        </div>
      </div>
    </section>
  );
}
